import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList } from '@fortawesome/pro-solid-svg-icons';

import AddControl from 'components/add_control';
import Default from './default';
import { Button } from 'components/ui/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import Modal from './modal';
import { defaultCategoryName } from './utils';
import { saveDefaultDeduction } from './api';

export default function DefaultDeductionSettings({ categories, defaultDeductions, features, options }) {
  if (!features.defaultDeductions) {
    return null;
  }

  const [defaults, setDefaults] = useState(defaultDeductions);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const categoryNames = [...categories.map(category => category.name), defaultCategoryName];

    const fixedDefaults = [];
    const updatedDefaults = defaults.map((defaultDeduction) => {
      if (!categoryNames.includes(defaultDeduction.category)) {
        defaultDeduction = { ...defaultDeduction, category: defaultCategoryName };
        fixedDefaults.push(defaultDeduction);
      }

      return defaultDeduction;
    });

    if (fixedDefaults.length > 0) {
      setDefaults(updatedDefaults);
      for (const fixedDefault of fixedDefaults) {
        saveDefaultDeduction(fixedDefault);
      }
    }
  }, [categories]);

  function addDefault(newDefault) {
    setDefaults([...defaults, newDefault]);
  }

  function updateDefault(updatedDefault) {
    setDefaults(defaults.map((defaultDeduction) => {
      return defaultDeduction.id === updatedDefault.id ? updatedDefault : defaultDeduction;
    }));
  }

  function removeDefault(deletedDefault) {
    setDefaults(defaults.filter(defaultDeduction => defaultDeduction.id !== deletedDefault.id));
  }


  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center gap-2">
        <h5>Default Deductions</h5>
        {defaults.length > 0 &&
          <AddControl name="add-default-deduction" onClick={() => setShowModal(true)}>
            Add Default Deduction
          </AddControl>
        }
      </div>

      {defaults.length === 0 &&
        <Empty className="bg-white">
          <Empty.Media>
            <FontAwesomeIcon icon={faList} />
          </Empty.Media>
          <Empty.Content>No defaults have been added yet</Empty.Content>
          <Empty.Actions>
            <Button label="Add Default Deduction" onClick={() => setShowModal(true)} />
          </Empty.Actions>
        </Empty>
      }

      {defaults.length > 0 &&
        <div className="table-container">
          <table className="table table-sticky">
            <thead>
              <tr>
                <th>Category</th>
                <th>Description</th>
                <th>Deduction Type</th>
                {features.transactionSideDetails && <th>Transaction Side</th>}
                <th>Rules</th>
                <th>Amount</th>
                <th className="sticky-cell end-cell" />
              </tr>
            </thead>
            <tbody>
              {defaults.map(defaultDeduction => (
                <Default
                  categories={categories}
                  defaultDeduction={defaultDeduction}
                  features={features}
                  key={defaultDeduction.id}
                  options={options}
                  onChange={updateDefault}
                  onDelete={removeDefault}
                />
              ))}
            </tbody>
          </table>
        </div>
      }

      <Modal
        categories={categories}
        features={features}
        options={options}
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={addDefault}
      />
    </div>
  );
}

DefaultDeductionSettings.propTypes = {
  categories: PropTypes.array,
  defaultDeductions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
  })).isRequired,
  features: PropTypes.shape({
    defaultDeductions: PropTypes.bool,
    transactionSideDetails: PropTypes.bool,
  }).isRequired,
  options: PropTypes.object,
};
